import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import { useAuth } from "@/lib/auth-provider";
import { authClient } from "@/lib/auth-client";
import { useApi, useApiMutation } from "./use-api";
import type {
  WebinarLayoutPresetDefinition,
  WebinarLayoutPreset,
  WebinarLayoutTransition,
  UnsupportedVisionWebinarLayoutPresetDefinition,
} from "@ease/shared";

export type LayoutPreset = WebinarLayoutPreset;

export interface ControlRoomStats {
  elapsedSeconds: number;
  attendeeCount: number;
  chatCount: number;
  qaQueueCount: number;
  pollsLaunched: number;
  offersShown: number;
  currentLayout: LayoutPreset;
}

export interface ProgramLayoutRuntimeState {
  currentLayout: LayoutPreset | null;
  currentLayoutUpdatedAt?: string | null;
  currentLayoutSource?: "control_room" | "run_of_show" | null;
  currentLayoutRevision?: number | null;
  storedLayout: LayoutPreset;
  storedLayoutUpdatedAt?: string | null;
  runtimeAvailable: boolean;
  runtimeUnavailableReason?: string | null;
}

export interface ControlRoomLayoutState {
  preset: LayoutPreset;
  transition: WebinarLayoutTransition;
  customConfig?: Record<string, unknown> | null;
  updatedAt: string;
}

export interface ControlRoomLayoutSwitchResult {
  preset: LayoutPreset;
  transition: WebinarLayoutTransition;
  keyboardShortcuts?: Record<string, LayoutPreset>;
}

export type ControlRoomLayoutSwitchMutationResult =
  | { ok: true; layout: ControlRoomLayoutSwitchResult }
  | { ok: false; error: string };

export interface WebinarSlideAsset {
  id: string;
  name: string;
  r2Key: string;
  url: string;
  contentType: string;
  sizeBytes: number;
}

export interface WebinarSlideDeckState {
  title: string;
  slides: WebinarSlideAsset[];
  currentIndex: number;
  updatedAt: string;
}

export interface WebinarSlideCurrentState extends WebinarSlideDeckState {
  currentSlide: WebinarSlideAsset | null;
}

export interface EngagementData {
  score: number;
  factors: {
    chatMessagesPerMinute: number;
    pollParticipationPercent: number;
    canvasActivityPercent: number;
    attentionScore: number;
    dropOffPercent: number;
  };
  warning: string | null;
  updatedAt: string;
}

export interface CopilotSuggestion {
  id: string;
  type: "launch_poll" | "show_offer" | "address_question" | "switch_layout" | "tech_issue" | "engagement_drop";
  priority: "high" | "medium" | "low";
  message: string;
  actionLabel?: string;
  actionPayload?: Record<string, unknown>;
  dismissedAt?: string;
}

export interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  senderRole: "attendee" | "presenter" | "moderator";
  text: string;
  timestamp: string;
  isPinned?: boolean;
  avatarUrl?: string | null;
  isModerated?: boolean;
  shadowFiltered?: boolean;
  moderationCategory?: string | null;
}

export interface ChatModerationSettings {
  bannedWords: string[];
  rateLimitPerMinute: number;
  linkPolicy: "block" | "hold" | "allow";
  emojiFloodMax: number;
  capsRatioMax: number;
  duplicateWindowSeconds: number;
  homoglyphNormalize: boolean;
  zeroWidthStrip: boolean;
  zalgoStripDepth: number;
  raidJoinsPerMinuteThreshold: number;
  raidAction: "slow" | "lockdown" | "hold";
  classifierFallback: "hold" | "restrict";
  holdQueueEnabled: boolean;
}

export interface ModeratedParticipant {
  participantId: string;
  displayName: string | null;
  isMuted: boolean;
  isBanned: boolean;
  isShadowFiltered: boolean;
  updatedAt: string;
  updatedBy: string | null;
}

export interface ChatModerationState {
  settings: ChatModerationSettings;
  participants: ModeratedParticipant[];
  queue: Array<{
    id: string;
    messageId: string | null;
    participantId: string;
    displayName: string | null;
    content: string;
    reasonCodes: string[];
    classifierScore: number | null;
    classifierError: string | null;
    status: "held" | "approved" | "rejected" | "undone";
    createdAt: string;
    decidedAt: string | null;
    decidedBy: string | null;
  }>;
  actions: Array<{
    id: string;
    action: string;
    participantId: string | null;
    messageId: string | null;
    queueItemId: string | null;
    reasonCodes: string[];
    notes: string | null;
    actorId: string;
    createdAt: string;
  }>;
  reports: Array<{
    id: string;
    reporterId: string;
    participantId: string;
    messageId: string | null;
    reasonCode: string;
    notes: string | null;
    status: "open" | "resolved" | "dismissed";
    createdAt: string;
  }>;
  raid: {
    enabled: boolean;
    action: "slow" | "lockdown" | "hold";
    triggeredAt: string | null;
    expiresAt: string | null;
    joinsObserved: number;
  };
}

export interface QaQuestion {
  id: string;
  question: string;
  askedBy: string;
  upvotes: number;
  isAnswered: boolean;
  answeredAt?: string;
  timestamp: string;
}

export interface WebinarPollOption {
  id: string;
  text: string;
}

export interface WebinarPollRecord {
  id: string;
  question: string;
  pollType: "single_choice" | "multiple_choice" | "word_cloud" | "rating" | "open_text";
  options?: WebinarPollOption[];
  isAnonymous?: boolean;
  showResultsToAttendees?: boolean;
  status: "draft" | "active" | "closed" | "archived";
  launchedAt?: string | null;
  closedAt?: string | null;
  sortOrder?: number;
}

export interface WebinarPollResults {
  pollId: string;
  pollType: WebinarPollRecord["pollType"];
  totalResponses: number;
  counts?: Array<{ optionId: string; label: string; responses: number }>;
  average?: number | null;
  textResponses?: string[];
}

export interface CreateWebinarPollInput {
  question: string;
  pollType: WebinarPollRecord["pollType"];
  options?: WebinarPollOption[];
  isAnonymous: boolean;
  showResultsToAttendees: boolean;
}

export interface AttendeeInfo {
  id: string;
  name: string;
  email: string;
  joinedAt: string;
  engagementScore: number;
  isActive: boolean;
  lastActivityAt: string;
}

export interface RaisedHandEntry {
  participantId: string;
  displayName: string | null;
  raisedAt: string | null;
  spotlightedAt?: string | null;
}

export interface SpotlightState {
  participantId: string | null;
  displayName: string | null;
  updatedAt: string | null;
}

interface ChatApiRecord {
  id: string;
  senderId: string | null;
  senderName: string;
  senderRole: "host" | "co_host" | "moderator" | "attendee";
  content: string;
  createdAt: string;
  avatarUrl?: string | null;
  isModerated?: boolean;
  shadowFiltered?: boolean;
  moderationCategory?: string | null;
}

interface QuestionApiRecord {
  id: string;
  contactId: string;
  question: string;
  answeredAt: string | null;
  upvotes: number;
  createdAt: string;
}

interface AttendeeApiRecord {
  id: string;
  personId: string;
  joinedAt: string;
  leftAt: string | null;
  watchDurationSeconds: number;
  engagementScore: number | null;
}

interface RealtimeEnvelope {
  type?: string;
  payload?: Record<string, unknown>;
}

function mapSenderRole(senderRole: ChatApiRecord["senderRole"]): ChatMessage["senderRole"] {
  if (senderRole === "moderator") {
    return "moderator";
  }

  if (senderRole === "host" || senderRole === "co_host") {
    return "presenter";
  }

  return "attendee";
}

function buildQuestionAskedBy(contactId: string): string {
  return contactId ? `Attendee ${contactId.slice(0, 8)}` : "Attendee";
}

function buildAttendeeLabel(personId: string): string {
  return personId ? `Attendee ${personId.slice(0, 8)}` : "Attendee";
}

export function useControlRoomStats(webinarId: string | undefined) {
  return useApi<ControlRoomStats>(
    webinarId ? `/v1/webinars/${webinarId}/control-room/stats` : "",
  );
}

export function useLayoutPresets(webinarId: string | undefined) {
  return useApi<{
    presets: WebinarLayoutPresetDefinition[];
    unsupportedVisionPresets?: UnsupportedVisionWebinarLayoutPresetDefinition[];
  }>(
    webinarId ? `/v1/webinars/${webinarId}/control-room/layout/presets` : "",
  );
}

export function useControlRoomLayout(webinarId: string | undefined) {
  return useApi<ControlRoomLayoutState>(
    webinarId ? `/v1/webinars/${webinarId}/control-room/layout` : "",
  );
}

export function useProgramLayoutRuntime(webinarId: string | undefined) {
  return useApi<ProgramLayoutRuntimeState>(
    webinarId ? `/v1/webinars/${webinarId}/control-room/program-layout` : "",
  );
}

export function useSlides(webinarId: string | undefined) {
  return useApi<WebinarSlideDeckState | null>(
    webinarId ? `/v1/webinars/${webinarId}/slides` : "",
  );
}

export function usePollManager(webinarId: string | undefined) {
  const pollQuery = useApi<WebinarPollRecord[]>(
    webinarId ? `/v1/webinars/${webinarId}/polls` : "",
  );
  const [polls, setPolls] = useState<WebinarPollRecord[]>([]);
  const [resultsById, setResultsById] = useState<Record<string, WebinarPollResults | undefined>>({});
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    setPolls(sortPolls(pollQuery.data ?? []));
  }, [pollQuery.data]);

  useEffect(() => {
    if (!webinarId) {
      return;
    }

    const currentWebinarId = webinarId;
    let cancelled = false;

    async function connectRealtime() {
      const tokenMethod = (authClient as unknown as { token?: () => Promise<unknown> }).token;
      if (typeof tokenMethod !== "function") {
        return;
      }

      try {
        const result = await tokenMethod.call(authClient);
        const token = extractBearerToken(result);
        if (!token || cancelled) {
          return;
        }

        const ws = new WebSocket(buildRealtimeWebsocketUrl(resolveRealtimeBaseUrl(), token, currentWebinarId));
        wsRef.current = ws;
        ws.addEventListener("message", (event) => {
          const parsed = parseEnvelope(event.data);
          if (!parsed?.type || !parsed.payload) {
            return;
          }

          if (parsed.type === "webinar.poll.launched" || parsed.type === "webinar.poll.closed") {
            const poll = pickRecord(parsed.payload, "poll");
            if (poll) {
              setPolls((current) => mergePolls(current, [poll as unknown as WebinarPollRecord]));
            }
            return;
          }

          if (parsed.type === "webinar.poll.results") {
            const poll = pickRecord(parsed.payload, "poll");
            const results = pickRecord(parsed.payload, "results");
            if (poll) {
              setPolls((current) => mergePolls(current, [poll as unknown as WebinarPollRecord]));
            }
            if (results && typeof results.pollId === "string") {
              setResultsById((current) => ({
                ...current,
                [results.pollId as string]: results as unknown as WebinarPollResults,
              }));
            }
          }
        });
      } catch {
        // Realtime updates are best-effort; mutations still refetch truth from the API.
      }
    }

    void connectRealtime();

    return () => {
      cancelled = true;
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [webinarId]);

  const createAndLaunch = useCallback(async (input: CreateWebinarPollInput) => {
    if (!webinarId) {
      setError("Webinar is required");
      return null;
    }

    setError(null);
    try {
      const created = await apiFetch<{ data?: { id?: string } }>(`/v1/webinars/${webinarId}/polls`, {
        method: "POST",
        body: JSON.stringify(input),
      });
      const pollId = created.data?.id;
      if (!pollId) {
        throw new Error("Poll creation did not return an ID");
      }
      await apiFetch(`/v1/webinars/${webinarId}/polls/${pollId}/launch`, { method: "POST" });
      pollQuery.refetch();
      return pollId;
    } catch (err) {
      const message = err instanceof ApiError
        ? `${err.status}: ${err.message}`
        : err instanceof Error
          ? err.message
          : "Unknown error";
      setError(message);
      return null;
    }
  }, [pollQuery, webinarId]);

  const launchPoll = useCallback(async (pollId: string) => {
    if (!webinarId) {
      setError("Webinar is required");
      return null;
    }

    setError(null);
    try {
      await apiFetch(`/v1/webinars/${webinarId}/polls/${pollId}/launch`, { method: "POST" });
      pollQuery.refetch();
      return pollId;
    } catch (err) {
      const message = err instanceof ApiError
        ? `${err.status}: ${err.message}`
        : err instanceof Error
          ? err.message
          : "Unknown error";
      setError(message);
      return null;
    }
  }, [pollQuery, webinarId]);

  const closePoll = useCallback(async (pollId: string) => {
    if (!webinarId) {
      setError("Webinar is required");
      return null;
    }

    setError(null);
    try {
      await apiFetch(`/v1/webinars/${webinarId}/polls/${pollId}/close`, { method: "POST" });
      pollQuery.refetch();
      return pollId;
    } catch (err) {
      const message = err instanceof ApiError
        ? `${err.status}: ${err.message}`
        : err instanceof Error
          ? err.message
          : "Unknown error";
      setError(message);
      return null;
    }
  }, [pollQuery, webinarId]);

  return {
    data: polls,
    resultsById,
    isLoading: pollQuery.isLoading,
    error: pollQuery.error ?? error,
    createAndLaunch,
    launchPoll,
    closePoll,
    refetch: pollQuery.refetch,
  };
}

export function useEngagement(webinarId: string | undefined) {
  return useApi<EngagementData>(
    webinarId ? `/v1/webinars/${webinarId}/control-room/engagement` : "",
  );
}

export function useCopilotSuggestions(webinarId: string | undefined) {
  const statsQuery = useControlRoomStats(webinarId);
  const engagementQuery = useEngagement(webinarId);
  const chatQuery = useApi<ChatApiRecord[]>(
    webinarId ? `/v1/webinars/${webinarId}/chat?limit=20` : "",
  );
  const qaQuery = useApi<QuestionApiRecord[]>(
    webinarId ? `/v1/webinars/${webinarId}/questions` : "",
  );

  const [data, setData] = useState<CopilotSuggestion[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!webinarId) {
      setData(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    if (statsQuery.isLoading || engagementQuery.isLoading || chatQuery.isLoading || qaQuery.isLoading) {
      setIsLoading(true);
      return;
    }

    if (statsQuery.error || engagementQuery.error || chatQuery.error || qaQuery.error) {
      setIsLoading(false);
      setError(statsQuery.error ?? engagementQuery.error ?? chatQuery.error ?? qaQuery.error ?? "Failed to load presenter metrics");
      return;
    }

    if (!statsQuery.data || !engagementQuery.data || !chatQuery.data || !qaQuery.data) {
      return;
    }

    const unansweredQuestion = qaQuery.data
      .filter((question) => question.answeredAt === null)
      .sort((left, right) => right.upvotes - left.upvotes)[0] ?? null;

    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    void apiFetch<{ data: { suggestions: CopilotSuggestion[] } }>(
      `/v1/webinars/${webinarId}/copilot/analyze`,
      {
        method: "POST",
        signal: controller.signal,
        body: JSON.stringify({
          chatMessages: chatQuery.data.map((message) => ({
            text: message.content,
            senderId: message.senderId,
            senderRole: message.senderRole,
          })),
          chatSentiment: "neutral",
          confusedMessages: 0,
          currentAttendees: statsQuery.data.attendeeCount,
          peakAttendees: statsQuery.data.attendeeCount,
          dropOffCount: Math.round((engagementQuery.data.factors.dropOffPercent / 100) * Math.max(statsQuery.data.attendeeCount, 1)),
          engagementScore: engagementQuery.data.score,
          activePollParticipation: engagementQuery.data.factors.pollParticipationPercent,
          topUnansweredQuestion: unansweredQuestion
            ? { question: unansweredQuestion.question, upvotes: unansweredQuestion.upvotes }
            : null,
          canvasElementsAdded: Math.round(engagementQuery.data.factors.canvasActivityPercent / 10),
          ctaClickRate: 0,
          purchaseCount: 0,
          currentSlideMinutes: Math.floor(statsQuery.data.elapsedSeconds / 60),
          audioIssueReports: 0,
          videoIssueReports: 0,
          sensitivity: "medium",
        }),
      },
    )
      .then((result) => {
        setData(result.data.suggestions);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") {
          return;
        }

        const message = err instanceof ApiError
          ? `${err.status}: ${err.message}`
          : err instanceof Error
            ? err.message
            : "Unknown error";
        setError(message);
        setData(null);
      })
      .finally(() => {
        setIsLoading(false);
      });

    return () => controller.abort();
  }, [
    webinarId,
    statsQuery.data,
    statsQuery.error,
    statsQuery.isLoading,
    engagementQuery.data,
    engagementQuery.error,
    engagementQuery.isLoading,
    chatQuery.data,
    chatQuery.error,
    chatQuery.isLoading,
    qaQuery.data,
    qaQuery.error,
    qaQuery.isLoading,
  ]);

  return { data, isLoading, error };
}

export function useChatMessages(webinarId: string | undefined) {
  const query = useApi<ChatApiRecord[]>(
    webinarId ? `/v1/webinars/${webinarId}/chat?limit=50` : "",
  );

  return {
    ...query,
    data: query.data?.map((message) => ({
      id: message.id,
      senderId: message.senderId ?? "",
      senderName: message.senderName,
      senderRole: mapSenderRole(message.senderRole),
      text: message.content,
      timestamp: message.createdAt,
      avatarUrl: message.avatarUrl ?? null,
      isModerated: message.isModerated ?? false,
      shadowFiltered: message.shadowFiltered ?? false,
      moderationCategory: message.moderationCategory ?? null,
    })) ?? null,
  };
}

export function useSendChatMessage(webinarId: string | undefined) {
  const { session } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ChatMessage | null>(null);

  const mutate = useCallback(async (content: string) => {
    if (!webinarId) {
      setError("Webinar is required");
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await apiFetch<{ data: ChatApiRecord | null }>(`/v1/webinars/${webinarId}/chat`, {
        method: "POST",
        body: JSON.stringify({
          content,
          senderName: session?.user.name ?? "Presenter",
        }),
      });

      const nextMessage = result.data
        ? {
            id: result.data.id,
            senderId: result.data.senderId ?? "",
            senderName: result.data.senderName,
            senderRole: mapSenderRole(result.data.senderRole),
            text: result.data.content,
            timestamp: result.data.createdAt,
            avatarUrl: result.data.avatarUrl ?? null,
            isModerated: result.data.isModerated ?? false,
            shadowFiltered: result.data.shadowFiltered ?? false,
            moderationCategory: result.data.moderationCategory ?? null,
          }
        : null;

      setData(nextMessage);
      return nextMessage;
    } catch (err) {
      const message = err instanceof ApiError
        ? `${err.status}: ${err.message}`
        : err instanceof Error
          ? err.message
          : "Unknown error";
      setError(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [session?.user.name, webinarId]);

  return { mutate, isLoading, error, data };
}

export function useChatModeration(webinarId: string | undefined) {
  return useApi<ChatModerationState>(
    webinarId ? `/v1/webinars/${webinarId}/chat/moderation` : "",
  );
}

export function useUpdateChatModerationSettings(webinarId: string | undefined) {
  return useApiMutation<ChatModerationSettings, { data: ChatModerationState }>(
    webinarId ? `/v1/webinars/${webinarId}/chat/moderation/settings` : "",
    "PUT",
  );
}

function useParticipantModerationAction(
  webinarId: string | undefined,
  action: "mute" | "unmute" | "ban" | "unban" | "shadow" | "unshadow",
) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ModeratedParticipant | null>(null);

  const mutate = useCallback(async (participantId: string) => {
    if (!webinarId) {
      setError("Webinar is required");
      return null;
    }

    setIsLoading(true);
    setError(null);
    try {
      const result = await apiFetch<{ data: ModeratedParticipant | null }>(
        `/v1/webinars/${webinarId}/chat/moderation/participants/${participantId}/${action}`,
        { method: "POST", body: JSON.stringify({}) },
      );
      setData(result.data);
      return result.data;
    } catch (err) {
      const message = err instanceof ApiError
        ? `${err.status}: ${err.message}`
        : err instanceof Error
          ? err.message
          : "Unknown error";
      setError(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [action, webinarId]);

  return { mutate, isLoading, error, data };
}

export function useMuteParticipant(webinarId: string | undefined) {
  return useParticipantModerationAction(webinarId, "mute");
}

export function useUnmuteParticipant(webinarId: string | undefined) {
  return useParticipantModerationAction(webinarId, "unmute");
}

export function useBanParticipant(webinarId: string | undefined) {
  return useParticipantModerationAction(webinarId, "ban");
}

export function useUnbanParticipant(webinarId: string | undefined) {
  return useParticipantModerationAction(webinarId, "unban");
}

export function useShadowParticipant(webinarId: string | undefined) {
  return useParticipantModerationAction(webinarId, "shadow");
}

export function useUnshadowParticipant(webinarId: string | undefined) {
  return useParticipantModerationAction(webinarId, "unshadow");
}

export function useQaQuestions(webinarId: string | undefined) {
  const query = useApi<QuestionApiRecord[]>(
    webinarId ? `/v1/webinars/${webinarId}/questions` : "",
  );

  return {
    ...query,
    data: query.data?.map((question) => ({
      id: question.id,
      question: question.question,
      askedBy: buildQuestionAskedBy(question.contactId),
      upvotes: question.upvotes,
      isAnswered: question.answeredAt !== null,
      answeredAt: question.answeredAt ?? undefined,
      timestamp: question.createdAt,
    })) ?? null,
  };
}

export function useAttendees(webinarId: string | undefined) {
  const query = useApi<AttendeeApiRecord[]>(
    webinarId ? `/v1/webinars/${webinarId}/attendance` : "",
  );

  return {
    ...query,
    data: query.data?.map((attendee) => ({
      id: attendee.id,
      name: buildAttendeeLabel(attendee.personId),
      email: "",
      joinedAt: attendee.joinedAt,
      engagementScore: attendee.engagementScore ?? 0,
      isActive: attendee.leftAt === null,
      lastActivityAt: attendee.leftAt ?? attendee.joinedAt,
    })) ?? null,
  };
}

export function useHandRaiseQueue(webinarId: string | undefined) {
  const query = useApi<{ queue?: RaisedHandEntry[]; spotlightParticipantId?: string | null; spotlightDisplayName?: string | null; spotlightUpdatedAt?: string | null }>(
    webinarId ? `/v1/webinars/${webinarId}/hand-raise` : "",
  );
  const [queue, setQueue] = useState<RaisedHandEntry[]>([]);
  const [spotlight, setSpotlight] = useState<SpotlightState>({
    participantId: null,
    displayName: null,
    updatedAt: null,
  });
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    setQueue(query.data?.queue ?? []);
    setSpotlight({
      participantId: query.data?.spotlightParticipantId ?? null,
      displayName: query.data?.spotlightDisplayName ?? null,
      updatedAt: query.data?.spotlightUpdatedAt ?? null,
    });
  }, [query.data]);

  useEffect(() => {
    if (!webinarId) {
      return;
    }

    const currentWebinarId = webinarId;
    let cancelled = false;
    async function connectRealtime() {
      const browserProofToken = typeof window !== "undefined"
        ? (window as typeof window & { __WEBINAR_BROWSER_PROOF_TOKEN__?: string }).__WEBINAR_BROWSER_PROOF_TOKEN__
        : undefined;

      try {
        const token = browserProofToken?.trim()
          || await requestAuthBearerToken()
          || null;
        if (!token || cancelled) {
          return;
        }

        const ws = new WebSocket(
          buildRealtimeWebsocketUrl(resolveRealtimeBaseUrl(), token, currentWebinarId),
        );
        wsRef.current = ws;
        ws.addEventListener("message", (event) => {
          const parsed = parseEnvelope(event.data);
          if (!parsed?.type || !parsed.payload) {
            return;
          }

          if (parsed.type === "webinar.hand.raised") {
            const participant = pickRecord(parsed.payload, "participant");
            if (participant && typeof participant.participantId === "string") {
              setQueue((current) => mergeRaisedHands(current, [participant as unknown as RaisedHandEntry]));
            }
            return;
          }

          if (parsed.type === "webinar.hand.lowered") {
            const participantId = typeof parsed.payload.participantId === "string"
              ? parsed.payload.participantId
              : null;
            if (participantId) {
              setQueue((current) => current.filter((entry) => entry.participantId !== participantId));
            }
            return;
          }

          if (parsed.type === "webinar.hand.lowered_all") {
            setQueue([]);
            return;
          }

          if (parsed.type === "webinar.spotlight.changed") {
            const participantId = typeof parsed.payload.spotlightParticipantId === "string"
              ? parsed.payload.spotlightParticipantId
              : null;
            const displayName = typeof parsed.payload.spotlightDisplayName === "string"
              ? parsed.payload.spotlightDisplayName
              : null;
            const updatedAt = typeof parsed.payload.spotlightUpdatedAt === "string"
              ? parsed.payload.spotlightUpdatedAt
              : null;
            setSpotlight({ participantId, displayName, updatedAt });
            setQueue((current) => current.map((entry) => ({
              ...entry,
              spotlightedAt: entry.participantId === participantId ? updatedAt : null,
            })));
          }
        });
      } catch {
        // best-effort realtime overlay
      }
    }

    void connectRealtime();
    return () => {
      cancelled = true;
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [webinarId]);

  const lowerHand = useCallback(async (participantId: string) => {
    if (!webinarId) {
      return false;
    }
    await apiFetch(`/v1/webinars/${webinarId}/hand-raise/${participantId}`, { method: "DELETE" });
    query.refetch();
    return true;
  }, [query, webinarId]);

  const lowerAllHands = useCallback(async () => {
    if (!webinarId) {
      return false;
    }
    await apiFetch(`/v1/webinars/${webinarId}/hand-raise/all`, { method: "DELETE" });
    query.refetch();
    return true;
  }, [query, webinarId]);

  const spotlightParticipant = useCallback(async (participantId: string) => {
    if (!webinarId) {
      return false;
    }
    await apiFetch(`/v1/webinars/${webinarId}/hand-raise/spotlight/${participantId}`, { method: "POST" });
    query.refetch();
    return true;
  }, [query, webinarId]);

  const removeSpotlight = useCallback(async (participantId: string) => {
    if (!webinarId) {
      return false;
    }
    await apiFetch(`/v1/webinars/${webinarId}/hand-raise/spotlight/${participantId}`, { method: "DELETE" });
    query.refetch();
    return true;
  }, [query, webinarId]);

  return {
    data: {
      queue,
      spotlight,
    },
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    lowerHand,
    lowerAllHands,
    spotlightParticipant,
    removeSpotlight,
  };
}

async function requestAuthBearerToken(): Promise<string | null> {
  const tokenMethod = (authClient as unknown as { token?: () => Promise<unknown> }).token;
  if (typeof tokenMethod !== "function") {
    return null;
  }

  try {
    const result = await tokenMethod.call(authClient);
    return extractBearerToken(result);
  } catch {
    return null;
  }
}

function extractBearerToken(result: unknown): string | null {
  const candidates = [
    pickValue(result, ["data", "token"]),
    pickValue(result, ["token"]),
    pickValue(result, ["data", "accessToken"]),
    pickValue(result, ["accessToken"]),
  ];

  return candidates.find((value): value is string => typeof value === "string" && value.trim().length > 0) ?? null;
}

function resolveRealtimeBaseUrl(): string {
  const env = import.meta.env as Record<string, string | undefined>;
  return env.VITE_REALTIME_URL?.trim() || window.location.origin;
}

function buildRealtimeWebsocketUrl(baseUrl: string, token: string, webinarId: string) {
  const url = new URL(baseUrl);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = "/ws";
  url.searchParams.set("token", token);
  url.searchParams.set("channels", `webinar.${webinarId}.*`);
  return url.toString();
}

function parseEnvelope(raw: unknown): RealtimeEnvelope | null {
  try {
    return JSON.parse(typeof raw === "string" ? raw : String(raw)) as RealtimeEnvelope;
  } catch {
    return null;
  }
}

function mergeRaisedHands(current: RaisedHandEntry[], incoming: RaisedHandEntry[]) {
  const next = new Map<string, RaisedHandEntry>();
  for (const entry of current) {
    next.set(entry.participantId, entry);
  }
  for (const entry of incoming) {
    next.set(entry.participantId, entry);
  }
  return Array.from(next.values()).sort((left, right) => String(left.raisedAt ?? "").localeCompare(String(right.raisedAt ?? "")));
}

function pickValue(input: unknown, path: string[]): unknown {
  let current: unknown = input;
  for (const segment of path) {
    if (!current || typeof current !== "object" || !(segment in current)) {
      return null;
    }
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

function pickRecord(input: Record<string, unknown>, key: string): Record<string, unknown> | null {
  const value = input[key];
  return value && typeof value === "object" ? value as Record<string, unknown> : null;
}

function mergePolls(current: WebinarPollRecord[], incoming: WebinarPollRecord[]) {
  const byId = new Map<string, WebinarPollRecord>();
  for (const poll of current) {
    byId.set(poll.id, poll);
  }
  for (const poll of incoming) {
    byId.set(poll.id, poll);
  }
  return sortPolls([...byId.values()]);
}

function sortPolls(polls: WebinarPollRecord[]) {
  return [...polls].sort((left, right) => {
    const statusRank = getPollStatusRank(left.status) - getPollStatusRank(right.status);
    if (statusRank !== 0) {
      return statusRank;
    }
    return (right.sortOrder ?? 0) - (left.sortOrder ?? 0);
  });
}

function getPollStatusRank(status: WebinarPollRecord["status"]) {
  if (status === "active") return 0;
  if (status === "draft") return 1;
  if (status === "closed") return 2;
  return 3;
}

export function useSaveSlides(webinarId: string | undefined) {
  return useApiMutation<{
    title: string;
    slides: WebinarSlideAsset[];
    currentIndex: number;
  }, { data: WebinarSlideCurrentState }>(
    webinarId ? `/v1/webinars/${webinarId}/slides` : "",
    "PUT",
  );
}

export function useSetCurrentSlide(webinarId: string | undefined) {
  return useApiMutation<{ currentIndex: number }, { data: WebinarSlideCurrentState }>(
    webinarId ? `/v1/webinars/${webinarId}/slides/current` : "",
    "POST",
  );
}

export function usePresenterAction(webinarId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<{ executed: boolean; actionType: string; config?: Record<string, unknown> | null } | null>(null);

  async function mutate(input: { actionType: string; config?: Record<string, unknown> }) {
    setIsLoading(true);
    setError(null);

    try {
      const result = await apiFetch<{ data: { executed: boolean; actionType: string; config?: Record<string, unknown> | null } }>(
        `/v1/webinars/${webinarId}/control-room/action/${encodeURIComponent(input.actionType)}`,
        {
          method: "POST",
          body: JSON.stringify({ config: input.config }),
        },
      );
      setData(result.data);
      return result.data;
    } catch (err) {
      const message = err instanceof ApiError
        ? `${err.status}: ${err.message}`
        : err instanceof Error
          ? err.message
          : "Unknown error";
      setError(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }

  return { mutate, isLoading, error, data };
}

export function useSwitchLayout(webinarId: string | undefined) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ControlRoomLayoutSwitchResult | null>(null);

  async function mutate(input: {
    preset: LayoutPreset;
    transition?: WebinarLayoutTransition;
    customConfig?: Record<string, unknown>;
  }): Promise<ControlRoomLayoutSwitchMutationResult> {
    if (!webinarId) {
      return { ok: false, error: "Missing webinar id" };
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiFetch<{ data: ControlRoomLayoutSwitchResult }>(
        `/v1/webinars/${webinarId}/control-room/layout`,
        {
          method: "POST",
          body: JSON.stringify({
            preset: input.preset,
            transition: input.transition ?? "crossfade",
            customConfig: input.customConfig,
          }),
        },
      );

      const next = {
        preset: response.data.preset,
        transition: response.data.transition,
        keyboardShortcuts: response.data.keyboardShortcuts,
      } satisfies ControlRoomLayoutSwitchResult;
      setData(next);
      return { ok: true, layout: next };
    } catch (err) {
      const message = err instanceof ApiError
        ? `${err.status}: ${err.message}`
        : err instanceof Error
          ? err.message
          : "Unknown error";
      setError(message);
      return { ok: false, error: message };
    } finally {
      setIsLoading(false);
    }
  }

  return { mutate, isLoading, error, data };
}
